import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeSlash, EnvelopeSimple, Lock } from '@phosphor-icons/react';
import MfaEnrollmentWizard from '../components/auth/MfaEnrollmentWizard';
import { useSignupPolicy } from '../hooks/useSignupPolicy';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // MFA challenge state (Premortem D)
  const [mfaChallenge, setMfaChallenge] = useState(null);  // null OR { challengeToken, stage }
  const [enrollChallenge, setEnrollChallenge] = useState(null); // token when enrollment_required
  const [otp, setOtp] = useState('');
  const { login, loginMfa } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idleLogout = searchParams.get('reason') === 'idle';
  const { enabled: signupEnabled } = useSignupPolicy();

  useEffect(() => {
    if (idleLogout) {
      toast.info('Session expired due to inactivity. Please log in again.');
    }
  }, [idleLogout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        if (result.stage === 'enrollment_required') {
          // Mandatory MFA: first-time (or reset) users must complete the
          // full enrollment wizard before any session exists.
          setEnrollChallenge(result.challengeToken);
        } else {
          // Enrolled user — OTP / backup-code challenge.
          setMfaChallenge(result);
        }
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Smart detection: 6 digits → authenticator OTP; anything else
      // (e.g. XXXX-XXXX-XX, with or without dashes) → recovery code.
      const cleaned = otp.replace(/\s+/g, '');
      const isOtp = /^\d{6}$/.test(cleaned);
      const { usedBackupCode } = await loginMfa({
        challengeToken: mfaChallenge.challengeToken,
        otp: isOtp ? cleaned : undefined,
        backupCode: isOtp ? undefined : cleaned,
      });
      if (usedBackupCode) {
        toast.warning('Logged in with a recovery code — it can\'t be used again. Generate fresh codes from Account Security.');
      } else {
        toast.success('Welcome back!');
      }
      setMfaChallenge(null);
      setOtp('');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'enrollment_required') {
        // Server says this account still needs authenticator setup —
        // route into the enrollment wizard instead of a dead-end error.
        toast.info(detail.message || 'Authenticator setup is required to continue.');
        setEnrollChallenge(mfaChallenge.challengeToken);
        setMfaChallenge(null);
        setOtp('');
      } else {
        toast.error(typeof detail === 'string'
          ? detail
          : 'That code did not verify — try the current code from your app, or a recovery code.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    enrollChallenge ? (
      <MfaEnrollmentWizard
        challengeToken={enrollChallenge}
        email={email}
        onComplete={() => navigate('/dashboard')}
        onCancel={() => setEnrollChallenge(null)}
      />
    ) : (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">AZ</span>
            </div>
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              AZAI Billing
            </span>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                Welcome back
              </CardTitle>
              <CardDescription className="text-slate-500">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {!mfaChallenge ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">
                    Email
                  </Label>
                  <div className="relative">
                    <EnvelopeSimple
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@agency.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700">
                      Password
                    </Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-indigo-900 hover:text-indigo-700 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-900 hover:bg-indigo-800 h-11"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
              ) : (
              <form onSubmit={handleMfaSubmit} className="space-y-5" data-testid="mfa-challenge-form">
                <div className="rounded-md bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-900">
                  Enter the <strong>6-digit code</strong> from your authenticator app — or, if you
                  don&apos;t have your device, one of your <strong>recovery codes</strong> (looks like{' '}
                  <span className="font-mono">XXXX-XXXX-XX</span>).
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-slate-700">Verification code</Label>
                  <Input
                    id="otp"
                    maxLength={14}
                    placeholder="6-digit code or recovery code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.toUpperCase().replace(/[^0-9A-F-]/g, ''))}
                    className="tracking-widest text-center font-mono text-lg"
                    autoComplete="one-time-code"
                    required
                    data-testid="mfa-otp-input"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-indigo-900 hover:bg-indigo-800 h-11"
                  disabled={loading || otp.replace(/[^0-9A-F]/g, '').length < 6}
                  data-testid="mfa-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Verify and sign in'
                  )}
                </Button>
                <div className="flex items-center justify-end text-xs text-slate-500">
                  <button
                    type="button"
                    className="hover:text-slate-700 hover:underline"
                    onClick={() => { setMfaChallenge(null); setOtp(''); }}
                    data-testid="mfa-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
              )}

              <p className="mt-6 text-center text-sm text-slate-500">
                Don&apos;t have an account?{' '}
                {signupEnabled ? (
                  <Link
                    to="/register"
                    className="text-indigo-900 font-medium hover:underline"
                    data-testid="register-link"
                  >
                    Create one
                  </Link>
                ) : (
                  <span className="text-slate-400" data-testid="register-disabled">
                    Contact your administrator
                  </span>
                )}
              </p>
              {signupEnabled && (
                <p className="mt-2 text-center text-sm">
                  <Link
                    to="/signup"
                    className="text-indigo-600 font-medium hover:underline"
                    data-testid="agency-signup-link"
                  >
                    Register your agency
                  </Link>
                </p>
              )}
              <p className="mt-4 pt-4 border-t border-slate-200 text-center text-xs text-slate-400">
                <Link
                  to="/admin-recovery"
                  className="hover:text-slate-600 hover:underline"
                  data-testid="admin-recovery-link"
                >
                  AZAI Admin Password Recovery →
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Visual */}
      <div
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #312E81 0%, #1E1B4B 100%)',
        }}
      >
        <div className="max-w-lg text-center z-10">
          <h2
            className="text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Manrope' }}
          >
            Ohio Medicaid Billing Made Simple
          </h2>
          <p className="text-indigo-200 text-lg">
            Streamline your ODM, DODD, and Managed Care claims with intelligent
            workflows and auto-calculated units.
          </p>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-700/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl" />
      </div>
    </div>
    )
  );
};

export default LoginPage;
